"use client";

import dynamic from "next/dynamic";
import { useEffect, useState } from "react";
import type { BeforeInstallPromptEvent } from "@/components/PwaInstallController";
import type { PwaOfflineUpdateContent } from "@/lib/content/entries/pwa-offline-update";

const PwaInstallController = dynamic(() => import("@/components/PwaInstallController"), {
  ssr: false,
});
const PwaUpdateController = dynamic(() => import("@/components/pwa/PwaUpdateController"), {
  ssr: false,
});

type PwaControllersDeferredProps = {
  readonly updateCopy: PwaOfflineUpdateContent["update"];
};

const isInstallPromptEnabled = process.env.NEXT_PUBLIC_PWA_INSTALL_ENABLED === "true";

export default function PwaControllersDeferred({ updateCopy }: PwaControllersDeferredProps) {
  const [ready, setReady] = useState(false);
  const [installPrompt, setInstallPrompt] = useState<BeforeInstallPromptEvent | null>(null);

  useEffect(() => {
    if (!isInstallPromptEnabled) {
      return;
    }

    const onBeforeInstallPrompt = (event: Event) => {
      event.preventDefault();
      setInstallPrompt(event as BeforeInstallPromptEvent);
    };
    const onAppInstalled = () => setInstallPrompt(null);

    window.addEventListener("beforeinstallprompt", onBeforeInstallPrompt);
    window.addEventListener("appinstalled", onAppInstalled);

    return () => {
      window.removeEventListener("beforeinstallprompt", onBeforeInstallPrompt);
      window.removeEventListener("appinstalled", onAppInstalled);
    };
  }, []);

  useEffect(() => {
    const idleWindow = window as Partial<Window>;
    if (typeof idleWindow.requestIdleCallback === "function") {
      const idleId = idleWindow.requestIdleCallback(() => setReady(true), { timeout: 1200 });
      return () => idleWindow.cancelIdleCallback?.(idleId);
    }

    const timeoutId = globalThis.setTimeout(() => setReady(true), 1200);
    return () => globalThis.clearTimeout(timeoutId);
  }, []);

  if (!ready) {
    return null;
  }

  return (
    <>
      <PwaInstallController initialInstallPrompt={installPrompt} />
      <PwaUpdateController copy={updateCopy} />
    </>
  );
}
